import { useState, useMemo, useCallback, useEffect, useDeferredValue } from 'react';
import {
  useCreateSuperAdmin,
  useRemoveSuperAdmin,
  useSuperAdminList,
  useToggleSuperAdminStatus,
  useUpdateSuperAdmin,
} from '../hooks';
import type { SuperAdminFilters, SuperAdminRecord } from '../types';
import { SuperAdminTable } from '../components/SuperAdminTable';
import { SuperAdminFormModal } from '../components/SuperAdminFormModal';
import { debounce } from '@shared/utils/debounce';

export function SuperAdminListPage() {
  const [filters, setFilters] = useState<SuperAdminFilters>({ status: 'all', search: '' });
  const deferredFilters = useDeferredValue(filters);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingRecord, setEditingRecord] = useState<SuperAdminRecord | null>(null);

  const { data = [], isLoading } = useSuperAdminList(deferredFilters);
  const createMutation = useCreateSuperAdmin();
  const updateMutation = useUpdateSuperAdmin();
  const removeMutation = useRemoveSuperAdmin();
  const toggleStatusMutation = useToggleSuperAdminStatus();

  const isBusy =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    removeMutation.isLoading ||
    toggleStatusMutation.isLoading;

  const openCreateModal = useCallback(() => {
    setModalMode('create');
    setEditingRecord(null);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback((record: SuperAdminRecord) => {
    setModalMode('edit');
    setEditingRecord(record);
    setModalOpen(true);
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Xác nhận xóa ${selectedIds.length} Super Admin?`)) return;

    await Promise.all(selectedIds.map((id) => removeMutation.mutateAsync(id)));
    setSelectedIds([]);
  }, [selectedIds, removeMutation]);

  const handleDelete = useCallback(
    (record: SuperAdminRecord) => {
      if (!confirm(`Bạn chắc chắn muốn xóa Super Admin ${record.email}?`)) return;
      removeMutation.mutate(record.id);
    },
    [removeMutation]
  );

  const handleToggleStatus = useCallback(
    (record: SuperAdminRecord) => {
      const nextStatus = record.status === 'active' ? 'inactive' : 'active';
      toggleStatusMutation.mutate({ id: record.id, status: nextStatus });
    },
    [toggleStatusMutation]
  );

  // Debounce filter changes
  const debouncedHandleFilterChange = useMemo(
    () =>
      debounce((next: SuperAdminFilters) => {
        setFilters(next);
      }, 300),
    []
  );

  const handleFilterChange = useCallback(
    (next: SuperAdminFilters) => {
      debouncedHandleFilterChange(next);
    },
    [debouncedHandleFilterChange]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedHandleFilterChange.cancel();
    };
  }, [debouncedHandleFilterChange]);

  return (
    <div className="card">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="card-title mb-1">Quản lý Super Admins</h3>
          <p className="card-subtitle mb-0">
            Chỉ Super Admin mới truy cập và thao tác được trên các tài khoản quản trị cấp cao.
          </p>
        </div>
        <div className="toolbar-actions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={openCreateModal}
            style={{ padding: '6px 12px', borderRadius: '10px' }}
          >
            <span className="ti ti-plus" style={{ fontSize: '16px', marginRight: '6px' }} />
            Thêm Super Admin
          </button>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm"
            disabled={selectedIds.length === 0 || removeMutation.isLoading}
            onClick={handleBulkDelete}
            style={{ padding: '6px 12px', borderRadius: '10px' }}
          >
            <span className="ti ti-trash" style={{ fontSize: '16px', marginRight: '6px' }} />
            Xóa đã chọn
          </button>
        </div>
      </div>

      <SuperAdminTable
        data={data}
        isLoading={isLoading || isBusy}
        filters={filters}
        onFiltersChange={handleFilterChange}
        onEdit={openEditModal}
        onToggleStatus={handleToggleStatus}
        onDelete={handleDelete}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      <SuperAdminFormModal
        open={isModalOpen}
        mode={modalMode}
        initialData={editingRecord}
        onClose={() => setModalOpen(false)}
        isSubmitting={isBusy}
        onSubmitCreate={async (values) => {
          await createMutation.mutateAsync(values);
          setModalOpen(false);
        }}
        onSubmitUpdate={async (id, payload) => {
          await updateMutation.mutateAsync({ id, payload });
          setModalOpen(false);
        }}
      />
    </div>
  );
}